import { useMemo } from 'react';
import { z } from 'zod';
import { EmptyValidationMessage } from '@ninja/utilities';
import { PolicyType, getYearOfBirth } from '../../../../../common';
import {
	useDateOfBirthField,
	useGenderField,
	useMaritalStatusField,
	usePrincipalSalaryBandField,
} from '../../../members/members-form/fields';
import { prepareFieldError } from '../../../members/members-form/helpers/add-field-error';
import { useEmiratesIdField } from '../fields/useEmiratesIdNumberField';
import { IEidMembersForm } from '../types';

export const useEidPrincipalFieldsStructure = (isSalaryBandHidden?: boolean) => {
	const genderField = useGenderField<IEidMembersForm>();
	const emiratesIdField = useEmiratesIdField<IEidMembersForm>();
	const maritalStatusField = useMaritalStatusField<IEidMembersForm>();
	const principalSalaryBandField = usePrincipalSalaryBandField<IEidMembersForm>(isSalaryBandHidden);
	const dateOfBirthField = useDateOfBirthField<IEidMembersForm>();
	const fieldsStructure = useMemo(
		() => [
			emiratesIdField.getField({ field: 'principalDetails.emiratesIdNumber' }),
			maritalStatusField.getField({ field: 'principalDetails.maritalStatusId' }),
			genderField.getField({ field: 'principalDetails.genderId' }),
			dateOfBirthField.getField({ field: 'principalDetails.dateOfBirth', fieldType: 'adult' }),
			principalSalaryBandField.getField({ field: 'principalDetails.salaryBandId' }),
		],
		[maritalStatusField.getField, emiratesIdField.getField, genderField.getField, dateOfBirthField.getField]
	);

	const getValidations = ({ formValues }: { formValues: IEidMembersForm }) =>
		z.object({
			principalDetails: z
				.object({
					maritalStatusId: z.number().optional(),
					genderId: z.number().optional(),
					emiratesIdNumber: emiratesIdField.getValidation(),
					memberId: z.string().optional(),
					salaryBandId: z.number().optional(),
					dateOfBirth: z.string().optional(),
				})
				.superRefine((values, ctx) => {
					const { maritalStatusId, salaryBandId, emiratesIdNumber, genderId, dateOfBirth } = values;
					const addFieldError = prepareFieldError(ctx);

					if (!emiratesIdNumber) addFieldError({ field: 'emiratesIdNumber', message: EmptyValidationMessage });

					emiratesIdField.validate(emiratesIdNumber, addFieldError, getYearOfBirth(dateOfBirth), ctx);

					if (!dateOfBirth) addFieldError({ field: 'dateOfBirth', message: EmptyValidationMessage });

					if (!genderId) addFieldError({ field: 'genderId', message: EmptyValidationMessage });

					if (!maritalStatusId) addFieldError({ field: 'maritalStatusId', message: EmptyValidationMessage });

					if (!salaryBandId && formValues.policyTypeId !== PolicyType.DomesticWorker)
						addFieldError({ field: 'salaryBandId', message: EmptyValidationMessage });

					if (dateOfBirth)
						dateOfBirthField.getValidation({
							formValues,
							fieldType: 'adult',
							addFieldError,
							dateOfBirth,
						});
				}),
		});

	return {
		fieldsStructure,
		getValidations,
	};
};
